import { useEffect, useState } from "react";
import { Building2, Save, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

/**
 * Admin → Unit Settings.
 *
 * Edit Unit 01 and Unit 02 letterhead values (legal name, full address, GSTIN).
 * The chosen values are what appear on the PDF header / footer / GSTIN line
 * whenever a user has that unit selected as the active unit.
 */
export default function UnitsAdmin() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => api.get("/units").then((r) => setUnits(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setField = (key, field, value) => {
    setUnits((prev) => prev.map((u) => (u.key === key ? { ...u, [field]: value } : u)));
  };

  const save = async (u) => {
    setBusy((b) => ({ ...b, [u.key]: true }));
    try {
      await api.put(`/units/${u.key}`, {
        label: u.label,
        name: u.name,
        address: u.address,
        gst: u.gst,
        email: u.email,
        phone: u.phone,
        bank_account_name: u.bank_account_name,
        bank_account_number: u.bank_account_number,
        bank_name: u.bank_name,
        bank_branch: u.bank_branch,
        bank_ifsc: u.bank_ifsc,
      });
      toast.success(`${u.label} saved`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [u.key]: false }));
    }
  };

  if (loading) return <div className="p-8 text-slate-500 text-sm">Loading…</div>;

  return (
    <div data-testid="admin-units-page">
      <div className="mb-6">
        <div className="overline mb-1">Settings</div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
          <Building2 className="w-6 h-6 text-amber-500" /> Unit Settings
        </h1>
        <p className="text-xs text-slate-500 mt-2 max-w-2xl">
          Edit the legal name, address, and GSTIN for each unit. These values
          appear on every PDF letterhead and footer for that unit. After saving,
          regenerate any existing quotation/report PDF you want refreshed.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {units.map((u) => (
          <div key={u.key} className="bg-white border border-slate-200 p-5" data-testid={`unit-card-${u.key}`}>
            <div className="overline mb-1 text-amber-600">{u.key.toUpperCase()}</div>
            <h2 className="font-heading text-lg font-bold mb-4 border-b border-slate-200 pb-2">{u.label}</h2>

            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Display Label</Label>
                <Input
                  value={u.label}
                  onChange={(e) => setField(u.key, "label", e.target.value)}
                  className="rounded-none"
                  data-testid={`${u.key}-label`}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Legal Name (on letterhead)</Label>
                <Input
                  value={u.name}
                  onChange={(e) => setField(u.key, "name", e.target.value)}
                  className="rounded-none"
                  data-testid={`${u.key}-name`}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Full Postal Address</Label>
                <Textarea
                  rows={4}
                  value={u.address}
                  onChange={(e) => setField(u.key, "address", e.target.value)}
                  className="rounded-none text-sm"
                  data-testid={`${u.key}-address`}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">GSTIN</Label>
                <Input
                  value={u.gst}
                  onChange={(e) => setField(u.key, "gst", e.target.value.toUpperCase())}
                  placeholder="29AHKPH7026A1ZK"
                  maxLength={15}
                  className="rounded-none font-mono"
                  data-testid={`${u.key}-gst`}
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Leave blank to omit the GSTIN line on this unit's PDFs.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Contact Email (shown on PDFs)</Label>
                <Input
                  type="email"
                  value={u.email || ""}
                  onChange={(e) => setField(u.key, "email", e.target.value)}
                  placeholder="unit@example.com"
                  className="rounded-none"
                  data-testid={`${u.key}-email`}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Contact Phone (shown on PDFs)</Label>
                <Input
                  value={u.phone || ""}
                  onChange={(e) => setField(u.key, "phone", e.target.value)}
                  placeholder="8494900615"
                  className="rounded-none font-mono"
                  data-testid={`${u.key}-phone`}
                />
              </div>

              <div className="pt-4 mt-3 border-t border-slate-200">
                <div className="overline mb-2 text-amber-600">Bank Details (Quotation PDF)</div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Account Name</Label>
                    <Input
                      value={u.bank_account_name || ""}
                      onChange={(e) => setField(u.key, "bank_account_name", e.target.value)}
                      className="rounded-none"
                      data-testid={`${u.key}-bank-account-name`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Account Number</Label>
                    <Input
                      value={u.bank_account_number || ""}
                      onChange={(e) => setField(u.key, "bank_account_number", e.target.value)}
                      className="rounded-none font-mono"
                      data-testid={`${u.key}-bank-account-number`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Bank Name</Label>
                    <Input
                      value={u.bank_name || ""}
                      onChange={(e) => setField(u.key, "bank_name", e.target.value)}
                      className="rounded-none"
                      data-testid={`${u.key}-bank-name`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Branch</Label>
                    <Input
                      value={u.bank_branch || ""}
                      onChange={(e) => setField(u.key, "bank_branch", e.target.value)}
                      className="rounded-none"
                      data-testid={`${u.key}-bank-branch`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">IFSC Code</Label>
                    <Input
                      value={u.bank_ifsc || ""}
                      onChange={(e) => setField(u.key, "bank_ifsc", e.target.value.toUpperCase())}
                      className="rounded-none font-mono"
                      data-testid={`${u.key}-bank-ifsc`}
                    />
                  </div>
                </div>
              </div>
              <Button
                onClick={() => save(u)}
                disabled={busy[u.key]}
                className="rounded-none btn-amber w-full mt-2"
                data-testid={`save-${u.key}-btn`}
              >
                {busy[u.key] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save {u.label}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
